import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { toast } from '../../hooks/useToast';
import { DashboardSkeleton } from '../Skeleton';
import {
  FileText, CheckSquare, Wallet, Plus,
  Clock, AlertTriangle, TrendingUp, TrendingDown,
  Sparkles, ArrowRight, Bell, Check, RepeatIcon,
} from 'lucide-react';

interface Note { id: number; title: string; updated_at: string; }
interface Task { id: number; title: string; status: string; deadline: string | null; }
interface SummaryData { totals: { type: string; total: number }[]; }
interface PlannedExpense { id: number; title: string; amount: number; next_due: string; is_recurring: number; category_name: string | null; }
interface BudgetCard { id: number; balance: number; }
interface DailyEntry { date: string; expense: number; income: number; }

interface DashboardProps {
  onNavigate: (tab: 'notes' | 'tasks' | 'budget') => void;
}

export function DashboardView({ onNavigate }: DashboardProps) {
  const { user } = useAuthStore();
  const [recentNotes, setRecentNotes] = useState<Note[]>([]);
  const [totalNotes, setTotalNotes] = useState(0);
  const [activeTasks, setActiveTasks] = useState<Task[]>([]);
  const [overdueTasks, setOverdueTasks] = useState<Task[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [taskCounts, setTaskCounts] = useState({ total: 0, done: 0, inProgress: 0 });
  const [plannedExpenses, setPlannedExpenses] = useState<PlannedExpense[]>([]);
  const [budgetCards, setBudgetCards] = useState<BudgetCard[]>([]);
  const [dailyExpenses, setDailyExpenses] = useState<DailyEntry[]>([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const safeFetch = async (url: string) => {
      try { const r = await apiFetch(url); return await r.json(); }
      catch { return { success: false }; }
    };
    const [nData, tData, sData, pData, cData, dData] = await Promise.all([
      safeFetch('/api/notes'),
      safeFetch('/api/tasks'),
      safeFetch(`/api/budget/summary?from=${new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10)}&to=${new Date().toISOString().slice(0, 10)}`),
      safeFetch('/api/budget/planned'),
      safeFetch('/api/budget/cards'),
      safeFetch('/api/budget/daily?days=7'),
    ]);

    if (nData.success) {
      const allNotes = nData.data as Note[];
      setTotalNotes(allNotes.length);
      setRecentNotes(allNotes.slice(0, 5));
    }
    if (tData.success) {
      const tasks = tData.data as Task[];
      setActiveTasks(tasks.filter((t: Task) => t.status === 'in_progress').slice(0, 5));
      const now = new Date(new Date().toDateString());
      setOverdueTasks(tasks.filter((t: Task) => t.deadline && new Date(t.deadline) < now && t.status !== 'done').slice(0, 5));
      setTaskCounts({
        total: tasks.length,
        done: tasks.filter((t: Task) => t.status === 'done').length,
        inProgress: tasks.filter((t: Task) => t.status === 'in_progress').length,
      });
    }
    if (sData.success) setSummary(sData.data);
    if (pData.success) setPlannedExpenses(pData.data);
    if (cData.success) setBudgetCards(cData.data);
    if (dData.success) setDailyExpenses(dData.data);
    if (!nData.success && !tData.success && !sData.success && !pData.success && !cData.success && !dData.success) {
      toast('Не удалось загрузить данные', 'error');
    }
    setLoaded(true);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('app:refresh', handler);
    return () => window.removeEventListener('app:refresh', handler);
  }, [load]);

  const fmt = (n: number) => n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
  const totalIncome = summary?.totals.find(t => t.type === 'income')?.total ?? 0;
  const totalExpense = summary?.totals.find(t => t.type === 'expense')?.total ?? 0;
  // Balance = sum of all card/wallet balances
  const balance = budgetCards.length > 0
    ? budgetCards.reduce((s, c) => s + (c.balance || 0), 0)
    : totalIncome - (summary?.totals.find(t => t.type === 'expense')?.total ?? 0);

  const todayStr = new Date().toISOString().slice(0, 10);
  const duePlanned = plannedExpenses.filter(p => p.next_due <= todayStr);
  const upcomingPlanned = plannedExpenses.filter(p => p.next_due > todayStr).slice(0, 3);

  const markPaid = async (id: number) => {
    await apiFetch(`/api/budget/planned/${id}/pay`, { method: 'POST' });
    load();
  };

  const today = new Date();
  const greeting = today.getHours() < 6 ? 'Доброй ночи' : today.getHours() < 12 ? 'Доброе утро' : today.getHours() < 18 ? 'Добрый день' : 'Добрый вечер';
  const dateStr = today.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });

  if (!loaded) return <DashboardSkeleton />;

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Greeting */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <Sparkles size={24} className="text-indigo-400" />
            <h1 className="text-2xl font-bold text-white">{greeting}, {user?.username}!</h1>
          </div>
          <p className="text-slate-400 capitalize ml-9">{dateStr}</p>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {[
            { label: 'Заметка', icon: FileText, tab: 'notes' as const, color: 'from-indigo-600/20 to-indigo-600/5 border-indigo-500/20 hover:border-indigo-500/40' },
            { label: 'Задача', icon: CheckSquare, tab: 'tasks' as const, color: 'from-violet-600/20 to-violet-600/5 border-violet-500/20 hover:border-violet-500/40' },
            { label: 'Запись', icon: Wallet, tab: 'budget' as const, color: 'from-emerald-600/20 to-emerald-600/5 border-emerald-500/20 hover:border-emerald-500/40' },
          ].map((a) => (
            <button
              key={a.tab}
              onClick={() => onNavigate(a.tab)}
              className={`flex items-center gap-3 px-4 py-3 bg-gradient-to-br ${a.color} border rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.98]`}
            >
              <div className="p-2 bg-white/10 rounded-xl">
                <Plus size={16} className="text-white" />
              </div>
              <span className="text-sm font-medium text-white">{a.label}</span>
            </button>
          ))}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          <div className="bg-gradient-to-br from-indigo-600/15 to-transparent border border-indigo-500/20 rounded-2xl p-4">
            <FileText size={18} className="text-indigo-400 mb-2" />
            <p className="text-2xl font-bold text-white">{totalNotes}</p>
            <p className="text-xs text-slate-400">Заметок</p>
          </div>
          <div className="bg-gradient-to-br from-violet-600/15 to-transparent border border-violet-500/20 rounded-2xl p-4">
            <CheckSquare size={18} className="text-violet-400 mb-2" />
            <p className="text-2xl font-bold text-white">{taskCounts.inProgress}</p>
            <p className="text-xs text-slate-400">В работе</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-600/15 to-transparent border border-emerald-500/20 rounded-2xl p-4">
            <TrendingUp size={18} className="text-emerald-400 mb-2" />
            <p className="text-2xl font-bold text-emerald-300">+{fmt(totalIncome)} ₽</p>
            <p className="text-xs text-slate-400">Доход</p>
          </div>
          <div className="bg-gradient-to-br from-red-600/15 to-transparent border border-red-500/20 rounded-2xl p-4">
            <TrendingDown size={18} className="text-red-400 mb-2" />
            <p className="text-2xl font-bold text-red-300">−{fmt(totalExpense)} ₽</p>
            <p className="text-xs text-slate-400">Расход</p>
          </div>
          <div className={`bg-gradient-to-br ${balance >= 0 ? 'from-blue-600/15 border-blue-500/20' : 'from-orange-600/15 border-orange-500/20'} to-transparent border rounded-2xl p-4`}>
            <Wallet size={18} className={balance >= 0 ? 'text-blue-400 mb-2' : 'text-orange-400 mb-2'} />
            <p className={`text-2xl font-bold ${balance >= 0 ? 'text-blue-300' : 'text-orange-300'}`}>{balance >= 0 ? '+' : ''}{fmt(balance)} ₽</p>
            <p className="text-xs text-slate-400">Общий баланс</p>
          </div>
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Recent notes */}
          <div className="bg-slate-900/50 border border-slate-700/50 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-indigo-400" />
                <h3 className="font-semibold text-white">Недавние заметки</h3>
              </div>
              <button onClick={() => onNavigate('notes')} className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                Все <ArrowRight size={12} />
              </button>
            </div>
            <div className="space-y-2">
              {recentNotes.map((n) => (
                <button key={n.id} onClick={() => onNavigate('notes')} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-800/50 transition-colors text-left">
                  <FileText size={14} className="text-slate-500 shrink-0" />
                  <span className="text-sm text-slate-300 truncate flex-1">{n.title || 'Без названия'}</span>
                  <span className="text-[10px] text-slate-600">{new Date(n.updated_at).toLocaleDateString('ru-RU')}</span>
                </button>
              ))}
              {recentNotes.length === 0 && <p className="text-sm text-slate-500 py-4 text-center">Нет заметок</p>}
            </div>
          </div>

          {/* Active + overdue tasks */}
          <div className="bg-slate-900/50 border border-slate-700/50 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CheckSquare size={16} className="text-violet-400" />
                <h3 className="font-semibold text-white">Задачи</h3>
              </div>
              <button onClick={() => onNavigate('tasks')} className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1">
                Все <ArrowRight size={12} />
              </button>
            </div>

            {overdueTasks.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <AlertTriangle size={12} className="text-red-400" />
                  <span className="text-xs font-medium text-red-400">Просрочено</span>
                </div>
                {overdueTasks.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-300 bg-red-500/5 rounded-lg mb-1">
                    <span className="truncate flex-1">{t.title}</span>
                    {t.deadline && <span className="text-[10px] text-red-400">{new Date(t.deadline).toLocaleDateString('ru-RU')}</span>}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-1.5">
              {activeTasks.map((t) => (
                <div key={t.id} className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-800/50 transition-colors">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                  <span className="text-sm text-slate-300 truncate flex-1">{t.title}</span>
                </div>
              ))}
              {activeTasks.length === 0 && overdueTasks.length === 0 && (
                <p className="text-sm text-slate-500 py-4 text-center">Нет активных задач</p>
              )}
            </div>

            {/* Progress */}
            {taskCounts.total > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-700/50">
                <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                  <span>Прогресс</span>
                  <span>{taskCounts.done} / {taskCounts.total}</span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-violet-600 to-indigo-500 rounded-full transition-all duration-700"
                    style={{ width: `${(taskCounts.done / taskCounts.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Planned expenses */}
        {(duePlanned.length > 0 || upcomingPlanned.length > 0) && (
          <div className="mt-6 bg-slate-900/50 border border-slate-700/50 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bell size={16} className="text-orange-400" />
                <h3 className="font-semibold text-white">Подписки и платежи</h3>
                {duePlanned.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs font-bold">{duePlanned.length} к оплате</span>
                )}
              </div>
              <button onClick={() => onNavigate('budget')} className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1">
                Все <ArrowRight size={12} />
              </button>
            </div>
            <div className="space-y-2">
              {duePlanned.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20">
                  <Bell size={14} className="text-red-400 shrink-0 animate-pulse" />
                  <span className="text-sm text-white flex-1 truncate">{p.title}</span>
                  {p.is_recurring ? <RepeatIcon size={12} className="text-slate-500" /> : null}
                  <span className="text-sm font-bold text-red-400">−{fmt(p.amount)} ₽</span>
                  <button onClick={() => markPaid(p.id)} className="px-2.5 py-1 rounded-lg bg-green-500/20 text-green-400 text-xs font-medium hover:bg-green-500/30 transition-colors flex items-center gap-1">
                    <Check size={12} /> Оплачено
                  </button>
                </div>
              ))}
              {upcomingPlanned.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-800/50 transition-colors">
                  <Clock size={14} className="text-slate-500 shrink-0" />
                  <span className="text-sm text-slate-300 flex-1 truncate">{p.title}</span>
                  {p.is_recurring ? <RepeatIcon size={12} className="text-slate-600" /> : null}
                  <span className="text-xs text-slate-500">{new Date(p.next_due).toLocaleDateString('ru-RU')}</span>
                  <span className="text-sm text-slate-400">−{fmt(p.amount)} ₽</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 7-day expenses mini chart */}
        {dailyExpenses.length > 0 && (
          <div className="mt-6 bg-slate-900/50 border border-slate-700/50 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendingDown size={16} className="text-red-400" />
                <h3 className="font-semibold text-white">Расходы за 7 дней</h3>
              </div>
              <button onClick={() => onNavigate('budget')} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
                Бюджет <ArrowRight size={12} />
              </button>
            </div>
            {/* Mini bar chart (SVG) */}
            {(() => {
              const maxVal = Math.max(...dailyExpenses.map(d => d.expense), 1);
              const todayEntry = dailyExpenses.find(d => d.date === todayStr);
              const todayExpense = todayEntry?.expense ?? 0;
              const chartH = 48;
              const barW = 28;
              const gap = 6;
              const totalW = dailyExpenses.length * (barW + gap) - gap;
              return (
                <div>
                  <div className="flex items-end gap-1.5 mb-2">
                    <div className="text-2xl font-bold text-red-300">−{fmt(todayExpense)} ₽</div>
                    <div className="text-xs text-slate-500 pb-1">сегодня</div>
                  </div>
                  <svg width={totalW} height={chartH + 20} className="w-full" viewBox={`0 0 ${totalW} ${chartH + 20}`}>
                    {dailyExpenses.map((d, i) => {
                      const barH = d.expense > 0 ? Math.max(3, (d.expense / maxVal) * chartH) : 2;
                      const x = i * (barW + gap);
                      const isToday = d.date === todayStr;
                      const dayLabel = new Date(d.date + 'T12:00:00').toLocaleDateString('ru-RU', { weekday: 'short' });
                      return (
                        <g key={d.date}>
                          <rect x={x} y={chartH - barH} width={barW} height={barH}
                            rx={4} fill={isToday ? '#ef4444' : '#ef444466'} />
                          <text x={x + barW / 2} y={chartH + 14} textAnchor="middle"
                            className="fill-slate-500 text-[9px]" style={{ fontSize: 9 }}>
                            {dayLabel}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
